import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CheckCircle, Pencil, RefreshCw, Type, Upload, XCircle } from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import MenuGuideButton from '../components/MenuGuideButton'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'

const EMPTY_FORM = {
  fontKey: '',
  displayName: '',
  licenseNote: '',
  active: true,
  files: [],
}

const FONT_WEIGHT_LABELS = {
  100: 'Thin',
  200: 'ExtraLight',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  800: 'ExtraBold',
  900: 'Black',
}

function unwrap(res) {
  return res?.data?.data !== undefined ? res.data.data : res?.data
}

function formatBytes(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return '-'
  if (n < 1024) return `${n.toLocaleString('ko-KR')} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat('ko-KR', { dateStyle: 'short', timeStyle: 'short' }).format(date)
}

function stripExtension(filename) {
  const name = String(filename || '').trim()
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(0, dot) : name
}

function inferDisplayName(files) {
  const first = Array.isArray(files) ? files[0] : null
  const base = stripExtension(first?.name)
  if (!base) return ''
  const cleaned = base.replace(/[-_\s]?[1-9]?[-_\s]?(extra[-_\s]?light|semi[-_\s]?bold|extra[-_\s]?bold|thin|light|regular|medium|black|bold)$/i, '')
  return cleaned || base
}

function detectWeight(filename) {
  const raw = String(filename || '').toLowerCase()
  const compact = raw.replace(/[\s_-]+/g, '')
  if (compact.includes('extralight') || compact.includes('ultralight')) return 200
  if (compact.includes('semibold') || compact.includes('demibold')) return 600
  if (compact.includes('extrabold') || compact.includes('ultrabold')) return 800
  if (compact.includes('thin')) return 100
  if (compact.includes('light')) return 300
  if (compact.includes('regular') || compact.includes('normal')) return 400
  if (compact.includes('medium')) return 500
  if (compact.includes('black') || compact.includes('heavy')) return 900
  if (compact.includes('bold')) return 700
  const match = raw.match(/(?:^|[^\d])([1-9]00)(?:[^\d]|$)/)
  return match ? Number(match[1]) : 400
}

function formatWeightList(weights) {
  const list = Array.isArray(weights) ? weights.filter(Boolean) : []
  if (list.length === 0) return '-'
  return list
    .map((weight) => `${weight} ${FONT_WEIGHT_LABELS[weight] || ''}`.trim())
    .join(', ')
}

export default function OpsFontManagementPage({ embedded = false }) {
  const { show } = useToast()
  const [fonts, setFonts] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [editing, setEditing] = useState(null)

  const stats = useMemo(() => {
    const active = fonts.filter((font) => font.active).length
    const system = fonts.filter((font) => font.systemFont).length
    return {
      total: fonts.length,
      active,
      custom: Math.max(0, fonts.length - system),
      system,
    }
  }, [fonts])

  const loadFonts = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/ops/report-output-fonts')
      const data = unwrap(res)
      setFonts(Array.isArray(data) ? data : [])
    } catch (err) {
      show(getApiErrorMessage(err, '폰트 목록을 불러오지 못했습니다.'), 'error')
    } finally {
      setLoading(false)
    }
  }, [show])

  useEffect(() => {
    void loadFonts()
  }, [loadFonts])

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }))

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || [])
    setForm((prev) => ({
      ...prev,
      files,
      displayName: prev.displayName || inferDisplayName(files),
    }))
  }

  const uploadFont = async (event) => {
    event.preventDefault()
    if (!form.files.length) {
      show('폰트 파일을 선택해 주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      const body = new FormData()
      form.files.forEach((file) => body.append('files', file))
      if (form.fontKey.trim()) body.append('fontKey', form.fontKey.trim())
      if (form.displayName.trim()) body.append('displayName', form.displayName.trim())
      if (form.licenseNote.trim()) body.append('licenseNote', form.licenseNote.trim())
      body.append('active', String(Boolean(form.active)))
      await api.post('/ops/report-output-fonts', body)
      show('폰트를 등록했습니다.', 'success')
      setForm(EMPTY_FORM)
      event.target.reset()
      await loadFonts()
    } catch (err) {
      show(getApiErrorMessage(err, '폰트 등록에 실패했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (font) => {
    setEditing({
      id: font.id,
      displayName: font.displayName || '',
      licenseNote: font.licenseNote || '',
      active: Boolean(font.active),
    })
  }

  const saveEdit = async (event) => {
    event.preventDefault()
    if (!editing?.id) return
    setSaving(true)
    try {
      await api.patch(`/ops/report-output-fonts/${editing.id}`, {
        displayName: editing.displayName,
        licenseNote: editing.licenseNote,
        active: Boolean(editing.active),
      })
      show('폰트 정보를 저장했습니다.', 'success')
      setEditing(null)
      await loadFonts()
    } catch (err) {
      show(getApiErrorMessage(err, '폰트 정보 저장에 실패했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (font) => {
    setSaving(true)
    try {
      await api.patch(`/ops/report-output-fonts/${font.id}`, { active: !font.active })
      show(font.active ? '폰트를 사용중지했습니다.' : '폰트를 사용하도록 변경했습니다.', 'success')
      await loadFonts()
    } catch (err) {
      show(getApiErrorMessage(err, '폰트 상태 변경에 실패했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className={embedded ? '' : 'mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8'}>
        <Loading />
      </div>
    )
  }

  return (
    <div className={embedded ? '' : 'mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8'}>
      {embedded ? (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={loadFonts}
            disabled={saving}
          >
            <RefreshCw className="size-4" />
            새로고침
          </button>
        </div>
      ) : (
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm text-slate-500">운영자 · 기타관리</div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">폰트관리</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <MenuGuideButton menuKey="MENU_OPS_OTHER" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              onClick={loadFonts}
              disabled={saving}
            >
              <RefreshCw className="size-4" />
              새로고침
            </button>
          </div>
        </div>
      )}

      <div className="mb-5 grid gap-3 sm:grid-cols-4">
        {[
          ['전체', stats.total],
          ['사용중', stats.active],
          ['업로드', stats.custom],
          ['기본', stats.system],
        ].map(([label, value]) => (
          <div key={label} className="rounded border border-slate-200 bg-white p-4">
            <div className="text-xs font-medium text-slate-500">{label}</div>
            <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
          </div>
        ))}
      </div>

      <div className="mb-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
        <form className="rounded border border-slate-200 bg-white p-4" onSubmit={uploadFont}>
          <div className="mb-4 flex items-center gap-2">
            <Upload className="size-5 text-slate-500" />
            <div className="font-semibold text-slate-900">폰트 등록</div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">
              폰트 파일
              <input
                type="file"
                multiple
                accept=".ttf,.otf,font/ttf,font/otf,application/vnd.ms-opentype"
                className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm"
                onChange={handleFileChange}
                disabled={saving}
              />
              {form.files.length > 0 ? (
                <div className="mt-2 rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  <div className="font-medium text-slate-700">{form.files.length}개 파일 선택됨</div>
                  <div className="mt-1 break-words">
                    {form.files.map((file) => `${detectWeight(file.name)} ${FONT_WEIGHT_LABELS[detectWeight(file.name)] || ''}`.trim()).join(', ')}
                  </div>
                </div>
              ) : null}
            </label>
            <label className="block text-sm font-medium text-slate-700">
              표시명
              <input
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={form.displayName}
                onChange={(e) => updateForm({ displayName: e.target.value })}
                disabled={saving}
                placeholder="예: Pretendard"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              폰트 키
              <input
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
                value={form.fontKey}
                onChange={(e) => updateForm({ fontKey: e.target.value })}
                disabled={saving}
                placeholder="미입력 시 자동 생성"
              />
            </label>
            <label className="flex items-end gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                className="mb-2 size-4"
                checked={form.active}
                onChange={(e) => updateForm({ active: e.target.checked })}
                disabled={saving}
              />
              등록 후 사용
            </label>
            <label className="block text-sm font-medium text-slate-700 md:col-span-2">
              라이선스 메모
              <textarea
                className="mt-1 min-h-[76px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={form.licenseNote}
                onChange={(e) => updateForm({ licenseNote: e.target.value })}
                disabled={saving}
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
              disabled={saving}
            >
              등록
            </button>
          </div>
        </form>

        <form className="rounded border border-slate-200 bg-white p-4" onSubmit={saveEdit}>
          <div className="mb-4 flex items-center gap-2">
            <Pencil className="size-5 text-slate-500" />
            <div className="font-semibold text-slate-900">선택 폰트 수정</div>
          </div>
          {editing ? (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">
                표시명
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={editing.displayName}
                  onChange={(e) => setEditing((prev) => ({ ...prev, displayName: e.target.value }))}
                  disabled={saving}
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                라이선스 메모
                <textarea
                  className="mt-1 min-h-[112px] w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={editing.licenseNote}
                  onChange={(e) => setEditing((prev) => ({ ...prev, licenseNote: e.target.value }))}
                  disabled={saving}
                />
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="size-4"
                  checked={editing.active}
                  onChange={(e) => setEditing((prev) => ({ ...prev, active: e.target.checked }))}
                  disabled={saving}
                />
                사용
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setEditing(null)} disabled={saving}>취소</button>
                <button type="submit" className="rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white" disabled={saving}>저장</button>
              </div>
            </div>
          ) : (
            <div className="flex min-h-[220px] items-center justify-center rounded bg-slate-50 text-sm text-slate-500">
              목록에서 수정할 폰트를 선택하세요.
            </div>
          )}
        </form>
      </div>

      <div className="overflow-hidden rounded border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          <Type className="size-5 text-slate-500" />
          <div className="font-semibold text-slate-900">등록 폰트</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">상태</th>
                <th className="px-4 py-3">표시명</th>
                <th className="px-4 py-3">폰트 키</th>
                <th className="px-4 py-3">파일</th>
                <th className="px-4 py-3">등록일</th>
                <th className="px-4 py-3 text-right">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {fonts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">등록된 폰트가 없습니다.</td>
                </tr>
              ) : fonts.map((font) => (
                <tr key={font.id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${font.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {font.active ? <CheckCircle className="size-3" /> : <XCircle className="size-3" />}
                        {font.active ? '사용중' : '중지'}
                      </span>
                      {font.systemFont && <span className="w-fit rounded-full bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700">기본</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{font.displayName || '-'}</div>
                    <div className="mt-1 text-xs text-slate-500">{font.familyName || '-'}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{font.fontKey}</td>
                  <td className="px-4 py-3">
                    <div className="text-slate-700">
                      {font.fileCount > 1
                        ? `${font.fileCount}개 파일`
                        : (font.originalFilename || (font.systemFont ? '서버 내장' : '-'))}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {Array.isArray(font.availableWeights) && font.availableWeights.length > 0
                        ? `굵기 ${formatWeightList(font.availableWeights)}`
                        : `${font.fontFormat || '-'} · ${formatBytes(font.fileSize)}`}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDateTime(font.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button type="button" className="rounded border px-2.5 py-1.5 text-xs hover:bg-slate-50" onClick={() => startEdit(font)} disabled={saving}>수정</button>
                      <button type="button" className="rounded border px-2.5 py-1.5 text-xs hover:bg-slate-50" onClick={() => toggleActive(font)} disabled={saving}>
                        {font.active ? '사용중지' : '사용'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
